import { LockOutlined as Lock } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemCodeInput } from "../../components/UIToolkit/Inputs/SystemCodeInput";
import { OTP_CODE_LENGTH, SubmitButton, useOtpCodeValidator } from "./common";
import { useVerifyOtpLogin } from "./hooks/useSignInMutations";
import { useWebOtpAutofill } from "./hooks/useWebOtpAutofill";

// Temporary-lock screen shown after too many failed OTP attempts (backend rate limit → 429).
export const LockedStep: FC<{ onBack: () => void }> = ({ onBack }) => {
	const { t } = useTranslation();

	return (
		<Box
			data-testid="signin-locked-screen"
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				textAlign: "center",
				gap: "1rem",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 72,
					height: 72,
					borderRadius: "50%",
					backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
				}}
			>
				<Lock sx={{ fontSize: "2rem", color: "error.main" }} />
			</Box>
			<Typography component="h2" sx={{ fontWeight: 600, fontSize: "1.125rem", color: "text.primary" }}>
				{t("otp.locked.title")}
			</Typography>
			<Typography sx={{ fontSize: "0.875rem", color: "text.secondary", maxWidth: "20rem" }}>{t("otp.locked.message")}</Typography>
			<Button data-testid="signin-locked-use-another-method" variant="text" size="small" onClick={onBack} sx={{ width: "fit-content", height: "1.5rem" }}>
				{t("otp.use.another.method")}
			</Button>
		</Box>
	);
};

interface OtpStepProps {
	username: string;
	onResend: () => void; // parent calls sendOtp(username); OtpStep just wires the button
	resendCooldown: number; // seconds remaining; button disabled + label when > 0
	// The parent owns the send-OTP mutation (initial send lives there); it forwards the in-flight
	// flag so the "Send again" button keeps its exact loading behavior after extraction.
	resendPending: boolean;
	onUseAnotherMethod: () => void; // parent switches back to the username step
	onLocked: () => void; // parent switches to its locked screen
}

export const OtpStep: FC<OtpStepProps> = ({ username, onResend, resendCooldown, resendPending, onUseAnotherMethod, onLocked }) => {
	const { t } = useTranslation();

	const [otpCode, setOtpCode] = useState("");

	// WebOTP autofill (Android) fills this input; the hook is armed below.
	const otpCodeInputRef = useRef<HTMLInputElement>(null);

	const verifyOtpLoginMutation = useVerifyOtpLogin({ onLocked });
	const validateOtpCode = useOtpCodeValidator();

	// Takes the code as an argument so a call never reads not-yet-flushed state.
	const verifyOtp = useCallback(
		async (code: string): Promise<void> => {
			if (!validateOtpCode(code)) {
				return;
			}
			const formattedUsername = username.includes("@") ? username.trim().toLowerCase() : username.trim();
			try {
				await verifyOtpLoginMutation.mutateAsync({ username: formattedUsername, code });
			} catch {
				// Error already handled in the mutation
			}
		},
		[username, validateOtpCode, verifyOtpLoginMutation],
	);

	// Manual "Continue" submit uses the current state value.
	const handleOtpVerify = useCallback(
		(e: React.SyntheticEvent<HTMLFormElement>) => {
			e.preventDefault();
			void verifyOtp(otpCode);
		},
		[verifyOtp, otpCode],
	);

	// Arm WebOTP autofill (Android) while the OTP step is shown. It fills the digits into the field;
	// the user still taps "Continue" (we don't auto-submit).
	useWebOtpAutofill(otpCodeInputRef, true);

	return (
		<Box component="form" sx={{ display: "contents" }} onSubmit={handleOtpVerify}>
			<Typography
				component="h2"
				sx={{
					alignSelf: "center",
					textAlign: "center",
					fontWeight: 600,
					fontSize: "1.125rem",
					color: "text.primary",
				}}
			>
				{t("otp.enter.code.title")}
			</Typography>
			<Typography
				sx={{
					alignSelf: "center",
					textAlign: "center",
					fontSize: "0.875rem",
					color: "text.secondary",
					mb: "0.5rem",
					wordBreak: "break-word",
				}}
			>
				{t("otp.sent.to", { username })}
			</Typography>
			<SystemCodeInput
				ref={otpCodeInputRef}
				dataTestId="signin-otp-code-input"
				name="code"
				length={OTP_CODE_LENGTH}
				value={otpCode}
				onChange={setOtpCode}
				disabled={verifyOtpLoginMutation.isPending}
				autoFocus
			/>
			<Box sx={{ display: "flex", justifyContent: "center", mt: "0.5rem" }}>
				<Button
					data-testid="signin-otp-resend-button"
					variant="text"
					size="small"
					onClick={onResend}
					loading={resendPending}
					disabled={resendCooldown > 0}
					sx={{ width: "fit-content", height: "1.5rem" }}
				>
					{resendCooldown > 0 ? t("otp.send.again.in", { seconds: resendCooldown }) : t("otp.send.again")}
				</Button>
			</Box>
			<SubmitButton data-testid="signin-otp-verify-button" disabled={verifyOtpLoginMutation.isPending || otpCode.length !== OTP_CODE_LENGTH}>
				{t("continue")}
			</SubmitButton>
			<Box sx={{ display: "flex", justifyContent: "center", mt: "0.25rem" }}>
				<Button
					data-testid="signin-otp-use-another-method"
					variant="text"
					size="small"
					onClick={() => {
						setOtpCode("");
						onUseAnotherMethod();
					}}
					disabled={verifyOtpLoginMutation.isPending}
					sx={{ width: "fit-content", height: "1.5rem" }}
				>
					{t("otp.use.another.method")}
				</Button>
			</Box>
		</Box>
	);
};
